import { useEffect, useRef, useState } from "react";
import { Brush, Eraser, Undo2, Trash2, Download, Wand2, RefreshCw } from "lucide-react";
import { useT } from "@/i18n";
import useImageGen from "@/components/ailab/useImageGen";
import { studioField } from "@/components/ailab/Studio";

const COLORS = ["#1e3a8a", "#ef4444", "#f97316", "#facc15", "#22c55e", "#06b6d4", "#3b82f6", "#a855f7", "#ec4899", "#78350f", "#000000"];
const SIZES = [4, 10, 20];
const TX = {
  es: { title: "Dibuja tu idea y luego la IA la convierte en una imagen bonita.", desc: "Cuéntale a la IA qué dibujaste (ej: un gato astronauta en la luna)", improve: "Mejorar con IA", working: "La IA está pintando...", eraser: "Borrador", undo: "Deshacer", clear: "Borrar todo", save: "Guardar dibujo", mine: "Tu dibujo", ai: "Versión de la IA", error: "La IA no pudo mejorar tu dibujo, intenta otra vez", empty: "¡Primero dibuja algo!" },
  en: { title: "Draw your idea and then AI turns it into a beautiful picture.", desc: "Tell the AI what you drew (e.g. an astronaut cat on the moon)", improve: "Improve with AI", working: "AI is painting...", eraser: "Eraser", undo: "Undo", clear: "Clear all", save: "Save drawing", mine: "Your drawing", ai: "AI version", error: "AI couldn't improve your drawing, try again", empty: "Draw something first!" },
};

export default function DrawActivity() {
  const { lang } = useT();
  const tx = TX[lang] || TX.es;
  const { generate, loading } = useImageGen(tx.error, lang);
  const canvasRef = useRef(null);
  const drawing = useRef(false);
  const history = useRef([]);
  const [color, setColor] = useState(COLORS[1]);
  const [size, setSize] = useState(SIZES[1]);
  const [eraser, setEraser] = useState(false);
  const [desc, setDesc] = useState("");
  const [drawn, setDrawn] = useState(false);
  const [mine, setMine] = useState(null);
  const [result, setResult] = useState(null);

  const ctx = () => canvasRef.current.getContext("2d");
  const fillWhite = () => { const c = ctx(); c.fillStyle = "#ffffff"; c.fillRect(0, 0, 1024, 768); };
  useEffect(() => { const c = canvasRef.current.getContext("2d"); c.fillStyle = "#ffffff"; c.fillRect(0, 0, 1024, 768); }, []);

  const pos = (e) => {
    const r = canvasRef.current.getBoundingClientRect();
    return [((e.clientX - r.left) / r.width) * 1024, ((e.clientY - r.top) / r.height) * 768];
  };
  const down = (e) => {
    e.currentTarget.setPointerCapture?.(e.pointerId);
    history.current = [...history.current.slice(-14), ctx().getImageData(0, 0, 1024, 768)];
    drawing.current = true;
    const c = ctx(); const [x, y] = pos(e);
    c.strokeStyle = eraser ? "#ffffff" : color; c.lineWidth = size * 2; c.lineCap = "round"; c.lineJoin = "round";
    c.beginPath(); c.moveTo(x, y); c.lineTo(x + 0.1, y + 0.1); c.stroke();
    setDrawn(true);
  };
  const move = (e) => { if (!drawing.current) return; const [x, y] = pos(e); const c = ctx(); c.lineTo(x, y); c.stroke(); };
  const up = () => { drawing.current = false; };
  const undo = () => { const last = history.current.pop(); if (last) ctx().putImageData(last, 0, 0); if (!history.current.length) setDrawn(false); };
  const clear = () => { history.current = []; fillWhite(); setDrawn(false); setResult(null); setMine(null); };
  const download = (src, name) => { const a = document.createElement("a"); a.href = src; a.download = name; a.click(); };

  const improve = async () => {
    if (!drawn) return;
    const img = canvasRef.current.toDataURL("image/jpeg", 0.9);
    setMine(img);
    const what = desc.trim() || (lang === "es" ? "lo que se ve en el dibujo" : "what is shown in the drawing");
    const out = await generate(`Turn this child's hand drawing into a beautiful, colorful, polished kid-friendly illustration. Keep the same idea, shapes, characters and composition. The drawing shows: ${what}`, img, "draw", desc.trim() || tx.mine);
    if (out) setResult(out);
  };

  const tool = (on) => `rounded-full p-2.5 shadow border-4 ${on ? "border-blue-600 bg-sky-100" : "border-white bg-white"}`;
  return (
    <div className="space-y-4" data-testid="draw-activity">
      <p className="font-bold text-blue-900">{tx.title}</p>
      <div className="flex flex-wrap items-center gap-2">
        {COLORS.map((c) => (
          <button key={c} type="button" onClick={() => { setColor(c); setEraser(false); }} className={`w-9 h-9 rounded-full border-4 shadow ${color === c && !eraser ? "border-yellow-300 scale-110" : "border-white"}`} style={{ background: c }} aria-label={c} data-testid={`draw-color-${c.slice(1)}`} />
        ))}
        <span className="w-px h-8 bg-blue-200 mx-1" />
        {SIZES.map((s) => (
          <button key={s} type="button" onClick={() => setSize(s)} className={tool(size === s)} data-testid={`draw-size-${s}`}><Brush style={{ width: 8 + s, height: 8 + s }} className="text-blue-900" /></button>
        ))}
        <button type="button" onClick={() => setEraser((v) => !v)} className={tool(eraser)} aria-label={tx.eraser} data-testid="draw-eraser"><Eraser className="w-5 h-5 text-blue-900" /></button>
        <button type="button" onClick={undo} className={tool(false)} aria-label={tx.undo} data-testid="draw-undo"><Undo2 className="w-5 h-5 text-blue-900" /></button>
        <button type="button" onClick={clear} className={tool(false)} aria-label={tx.clear} data-testid="draw-clear"><Trash2 className="w-5 h-5 text-rose-600" /></button>
        <button type="button" onClick={() => download(canvasRef.current.toDataURL("image/png"), "mi-dibujo.png")} className={tool(false)} aria-label={tx.save} data-testid="draw-download"><Download className="w-5 h-5 text-blue-900" /></button>
      </div>
      <canvas ref={canvasRef} width={1024} height={768} onPointerDown={down} onPointerMove={move} onPointerUp={up} onPointerLeave={up}
        className="w-full max-w-3xl aspect-[4/3] bg-white rounded-3xl border-4 border-white shadow-lg touch-none cursor-crosshair" data-testid="draw-canvas" />
      <input value={desc} onChange={(e) => setDesc(e.target.value.slice(0, 200))} placeholder={tx.desc} className={studioField} data-testid="draw-desc" />
      <button type="button" onClick={improve} disabled={loading || !drawn} className="glossy-btn bg-yellow-300 text-blue-900 font-fredoka font-bold text-lg px-6 py-2.5 rounded-full inline-flex items-center gap-2 disabled:opacity-60" data-testid="draw-improve-btn">
        {loading ? <RefreshCw className="w-5 h-5 animate-spin" /> : <Wand2 className="w-5 h-5" />} {loading ? tx.working : drawn ? tx.improve : tx.empty}
      </button>
      {(mine || result) && (
        <div className="grid sm:grid-cols-2 gap-4">
          {mine && <figure><figcaption className="font-bold text-blue-950 mb-1">{tx.mine}</figcaption><img src={mine} alt="" className="w-full rounded-3xl border-4 border-white shadow" /></figure>}
          {result && (
            <figure className="relative"><figcaption className="font-bold text-blue-950 mb-1">{tx.ai}</figcaption>
              <img src={result} alt="" className="w-full rounded-3xl border-4 border-yellow-300 shadow-lg" data-testid="draw-result" />
              <button type="button" onClick={() => download(result, "mi-dibujo-ia.png")} className="absolute top-9 right-3 bg-white/90 rounded-full p-2 shadow" data-testid="draw-result-download"><Download className="w-5 h-5 text-blue-900" /></button>
            </figure>
          )}
        </div>
      )}
    </div>
  );
}
